import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiDollarSign, FiShoppingCart, FiRotateCcw, FiCreditCard,
  FiFilter, FiChevronUp, FiChevronDown, FiDownload, FiPrinter,
  FiSearch, FiTrendingUp
} from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { navigateToTransaction } from '../../utils/navigationHelper';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import { formatCurrency, formatNumber, formatDate } from '../../utils/formatters';
import useReportMemory from '../../hooks/useReportMemory';
import { addPrintBrand } from '../../utils/printBranding';

const POSSalesReport = () => {
  const { activeCompany, activeBranch } = useAuth();
  // Backward memory: restore filters + generated rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('pos-sales-report');
  const navigate = useNavigate();

  // Data state
  const [transactions, setTransactions] = useState(() => savedMem?.transactions ?? []);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(() => savedMem?.hasLoaded ?? false);

  // Filter state - default to current month
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? '');
  const [regions, setRegions] = useState([]);
  const [areas, setAreas] = useState([]);
  const [selectedRegionId, setSelectedRegionId] = useState(() => savedMem?.selectedRegionId ?? 'all');
  const [selectedAreaId, setSelectedAreaId] = useState(() => savedMem?.selectedAreaId ?? 'all');
  const [counters, setCounters] = useState([]);
  const [selectedCounterId, setSelectedCounterId] = useState(() => savedMem?.selectedCounterId ?? 'all');
  const [shifts, setShifts] = useState([]);
  const [selectedShiftId, setSelectedShiftId] = useState(() => savedMem?.selectedShiftId ?? 'all');
  const [selectedCustomerId, setSelectedCustomerId] = useState(() => savedMem?.selectedCustomerId ?? 'all');

  // Sort state
  const [sortField, setSortField] = useState(() => savedMem?.sortField ?? 'docDate');
  const [sortDirection, setSortDirection] = useState(() => savedMem?.sortDirection ?? 'desc');

  // Initialize default date range to current month (fresh visit only — a restored
  // snapshot keeps the user's own dates).
  useEffect(() => {
    if (!savedMem) {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateFrom(firstDay.toISOString().split('T')[0]);
      setDateTo(today.toISOString().split('T')[0]);
    }
  }, [savedMem]);

  // Load customers, regions, areas
  const loadMasterData = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const [custSnap, regSnap, areaSnap, counterSnap, shiftSnap] = await Promise.all([
        getDocs(query(collection(db, 'companies', activeCompany.id, 'customers'), orderBy('companyName', 'asc'))),
        getDocs(collection(db, 'companies', activeCompany.id, 'regions')),
        getDocs(collection(db, 'companies', activeCompany.id, 'areas')),
        getDocs(collection(db, 'companies', activeCompany.id, 'posCounters')),
        getDocs(collection(db, 'companies', activeCompany.id, 'posShifts')),
      ]);
      setCustomers(custSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setRegions(regSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setAreas(areaSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setCounters(counterSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setShifts(shiftSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading master data:', error);
    }
  }, [activeCompany]);

  useEffect(() => {
    loadMasterData();
  }, [loadMasterData]);

  // Customer map for region/area filtering
  const customerMap = useMemo(() => {
    const map = {};
    customers.forEach(c => { map[c.id] = c; });
    return map;
  }, [customers]);

  // Cascading areas based on selected region
  const filteredAreas = useMemo(() => {
    if (selectedRegionId === 'all') return areas;
    return areas.filter(a => a.regionId === selectedRegionId);
  }, [areas, selectedRegionId]);

  // Shift lookup + display label (e.g. "Shift 3 · Counter 1").
  const shiftMap = useMemo(() => {
    const m = {};
    shifts.forEach(s => { m[s.id] = s; });
    return m;
  }, [shifts]);
  const shiftLabel = (shiftId) => {
    const s = shiftMap[shiftId];
    if (!s) return '-';
    const num = s.shiftNumber != null ? `Shift ${s.shiftNumber}` : (s.shiftCode || 'Shift');
    return s.counterCode || s.counterName ? `${num} · ${s.counterCode || s.counterName}` : num;
  };
  const shiftOptionLabel = (s) => {
    const num = s.shiftNumber != null ? `Shift ${s.shiftNumber}` : (s.shiftCode || 'Shift');
    const ctr = s.counterCode || s.counterName ? ` · ${s.counterCode || s.counterName}` : '';
    const day = s.openedAt ? ` (${String(s.openedAt).split('T')[0]})` : '';
    return `${num}${ctr}${day}`;
  };

  // Load transactions
  const loadTransactions = useCallback(async () => {
    if (!activeCompany?.id) return;

    setLoading(true);
    setHasLoaded(true);

    try {
      const transRef = collection(db, 'companies', activeCompany.id, 'transactions');

      // Query PS transactions — date-bounded server-side (Tier 5, Firebase cost).
      // Uses the EXISTING (tranTypeCode, docDate) composite index, so this reads
      // only the shown date range instead of the entire POS history every open.
      const psConditions = [where('tranTypeCode', '==', 'PS')];
      if (dateFrom) psConditions.push(where('docDate', '>=', dateFrom));
      if (dateTo) psConditions.push(where('docDate', '<=', dateTo));
      const psQuery = query(transRef, ...psConditions);
      const psSnapshot = await getDocs(psQuery);
      let psTransactions = psSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).filter(t => {
        if (!activeBranch || activeBranch.id === 'all') return true;
        return t.branchId === activeBranch.id;
      });

      // Query SR transactions (only POS returns) — same server-side date bound.
      const srConditions = [where('tranTypeCode', '==', 'SR')];
      if (dateFrom) srConditions.push(where('docDate', '>=', dateFrom));
      if (dateTo) srConditions.push(where('docDate', '<=', dateTo));
      const srQuery = query(transRef, ...srConditions);
      const srSnapshot = await getDocs(srQuery);
      let srTransactions = srSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        })
        .filter(t => t.posTransaction === true);

      // Combine
      let allTransactions = [...psTransactions, ...srTransactions];

      // Apply date filters in memory
      if (dateFrom) {
        allTransactions = allTransactions.filter(t => (t.docDate || '') >= dateFrom);
      }
      if (dateTo) {
        allTransactions = allTransactions.filter(t => (t.docDate || '') <= dateTo);
      }

      // Apply region filter (walk-in sales without partyId are excluded when filtering)
      if (selectedRegionId !== 'all') {
        allTransactions = allTransactions.filter(t => {
          const cust = customerMap[t.partyId];
          return cust && cust.regionId === selectedRegionId;
        });
      }

      // Apply area filter
      if (selectedAreaId !== 'all') {
        allTransactions = allTransactions.filter(t => {
          const cust = customerMap[t.partyId];
          return cust && cust.areaId === selectedAreaId;
        });
      }

      // Apply counter filter
      if (selectedCounterId !== 'all') {
        allTransactions = allTransactions.filter(t => t.counterId === selectedCounterId);
      }

      // Apply shift filter
      if (selectedShiftId !== 'all') {
        allTransactions = allTransactions.filter(t => t.shiftId === selectedShiftId);
      }

      // Apply customer filter
      if (selectedCustomerId !== 'all') {
        allTransactions = allTransactions.filter(t => t.partyId === selectedCustomerId);
      }

      // Sort by docDate descending by default
      allTransactions.sort((a, b) => {
        const dateCompare = (b.docDate || '').localeCompare(a.docDate || '');
        if (dateCompare !== 0) return dateCompare;
        return (b.docNumber || '').localeCompare(a.docNumber || '');
      });

      setTransactions(allTransactions);
      // Remember this view so Back restores filters + rows with 0 re-reads.
      saveMemory({
        dateFrom, dateTo,
        selectedRegionId, selectedAreaId, selectedCounterId, selectedShiftId, selectedCustomerId,
        sortField, sortDirection,
        transactions: allTransactions,
        hasLoaded: true
      });
    } catch (error) {
      console.error('Error loading POS transactions:', error);
    } finally {
      setLoading(false);
    }
  }, [activeCompany, dateFrom, dateTo, selectedRegionId, selectedAreaId, selectedCounterId, selectedShiftId, selectedCustomerId, customerMap, activeBranch, sortField, sortDirection, saveMemory]);

  // Tier 5 (Firebase cost): NO auto-load. This report used to fetch the WHOLE
  // POS history on mount and again on every filter change. It now loads only
  // when the user clicks "Generate Report", and only for the selected date range.

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sorted transactions
  const sortedTransactions = useMemo(() => {
    const sorted = [...transactions];
    sorted.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle numeric fields
      if (['totalAmount', 'netAmount'].includes(sortField)) {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [transactions, sortField, sortDirection]);

  // Summary calculations — cancelled (voided) sales excluded from revenue
  const summary = useMemo(() => {
    const active = transactions.filter(t => t.status !== 'Cancelled');
    const sales = active.filter(t => t.tranTypeCode === 'PS');
    const returns = active.filter(t => t.tranTypeCode === 'SR');
    const voided = transactions.filter(t => t.tranTypeCode === 'PS' && t.status === 'Cancelled');

    const totalSalesAmount = sales.reduce((sum, t) => sum + (Number(t.netAmount) || Number(t.totalAmount) || 0), 0);
    const totalReturnsAmount = returns.reduce((sum, t) => sum + (Number(t.netAmount) || Number(t.totalAmount) || 0), 0);
    const voidedAmount = voided.reduce((sum, t) => sum + (Number(t.netAmount) || Number(t.totalAmount) || 0), 0);
    const netSales = totalSalesAmount - totalReturnsAmount;
    const averageSale = sales.length > 0 ? netSales / sales.length : 0;

    return {
      salesCount: sales.length,
      totalSalesAmount,
      returnsCount: returns.length,
      totalReturnsAmount,
      voidedCount: voided.length,
      voidedAmount,
      netSales,
      averageSale
    };
  }, [transactions]);

  // Payment breakdown
  const paymentBreakdown = useMemo(() => {
    let cashTotal = 0;
    let cardTotal = 0;
    let creditTotal = 0;

    transactions.forEach(t => {
      if (t.status === 'Cancelled') return; // voided sales don't count toward payments
      const payments = t.posPayments || [];
      const isReturn = t.tranTypeCode === 'SR';

      payments.forEach(p => {
        const amt = Number(p.amount) || 0;
        const effectiveAmt = isReturn ? -amt : amt;

        if (p.method === 'cash') {
          cashTotal += effectiveAmt;
        } else if (p.method === 'card') {
          cardTotal += effectiveAmt;
        } else if (p.method === 'credit') {
          creditTotal += effectiveAmt;
        }
      });
    });

    return { cashTotal, cardTotal, creditTotal };
  }, [transactions]);

  // Get primary payment method for display
  const getPaymentMethodDisplay = (transaction) => {
    const payments = transaction.posPayments || [];
    if (payments.length === 0) return transaction.paymentMethod || '-';
    if (payments.length === 1) {
      return payments[0].method ? payments[0].method.charAt(0).toUpperCase() + payments[0].method.slice(1) : '-';
    }
    // Multiple payment methods - show "Split"
    const methods = payments.map(p => p.method ? p.method.charAt(0).toUpperCase() + p.method.slice(1) : '').filter(Boolean);
    return methods.join(' / ');
  };

  // Per-transaction payment split (cash / card / credit) for the report columns.
  const getPaymentSplit = (t) => {
    const split = { cash: 0, card: 0, credit: 0 };
    (t.posPayments || []).forEach(p => {
      const amt = Number(p.amount) || 0;
      if (p.method === 'cash') split.cash += amt;
      else if (p.method === 'card') split.card += amt;
      else if (p.method === 'credit') split.credit += amt;
    });
    return split;
  };

  // Get items count
  const getItemsCount = (transaction) => {
    const lines = transaction.lines || transaction.items || [];
    return lines.length;
  };

  // CSV Export
  const handleExport = () => {
    if (sortedTransactions.length === 0) return;

    let csv = `POS Sales Report\n`;
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Period: ${dateFrom ? formatDate(dateFrom) : 'Start'} to ${dateTo ? formatDate(dateTo) : 'End'}\n`;
    csv += `Total Sales: ${formatCurrency(summary.totalSalesAmount)} (${summary.salesCount} transactions)\n`;
    csv += `Total Returns: ${formatCurrency(summary.totalReturnsAmount)} (${summary.returnsCount} transactions)\n`;
    csv += `Net Sales: ${formatCurrency(summary.netSales)}\n`;
    csv += `Cash: ${formatCurrency(paymentBreakdown.cashTotal)} | Card: ${formatCurrency(paymentBreakdown.cardTotal)} | Credit: ${formatCurrency(paymentBreakdown.creditTotal)}\n\n`;
    csv += `#,Date,Doc Number,Type,Shift,Customer,Items,Cash,Card,Credit,Balance,Amount\n`;

    sortedTransactions.forEach((t, idx) => {
      const amt = Number(t.netAmount) || Number(t.totalAmount) || 0;
      const isReturn = t.tranTypeCode === 'SR';
      const sign = isReturn ? -1 : 1;
      const s = getPaymentSplit(t);
      csv += `${idx + 1},`;
      csv += `${formatDate(t.docDate)},`;
      csv += `${t.docNumber || ''},`;
      csv += `${isReturn ? 'Return' : 'Sale'},`;
      csv += `"${shiftLabel(t.shiftId).replace(/"/g, '""')}",`;
      csv += `"${(t.partyName || 'Walk-in Customer').replace(/"/g, '""')}",`;
      csv += `${getItemsCount(t)},`;
      csv += `${sign * s.cash},`;
      csv += `${sign * s.card},`;
      csv += `${sign * s.credit},`;
      csv += `${Number(t.balanceAmount) || 0},`;
      csv += `${isReturn ? -amt : amt}\n`;
    });

    csv += `\n,,,,,,,${paymentBreakdown.cashTotal},${paymentBreakdown.cardTotal},${paymentBreakdown.creditTotal},,${summary.netSales}\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `POS_Sales_Report_${dateFrom || 'all'}_to_${dateTo || 'all'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Print
  const handlePrint = () => {
    if (sortedTransactions.length === 0) return;

    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>POS Sales Report</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:12px}
        h1{color:#800020;font-size:20px;margin-bottom:5px}
        .meta{color:#666;font-size:12px;margin-bottom:15px}
        .summary{display:flex;gap:24px;margin-bottom:20px;padding:12px;background:#f9f9f9;border-radius:8px;flex-wrap:wrap}
        .summary-item{text-align:center;min-width:120px}
        .summary-label{font-size:10px;color:#666;margin-bottom:3px}
        .summary-value{font-size:14px;font-weight:600}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#800020;color:white;padding:6px 8px;text-align:left;font-size:10px;text-transform:uppercase}
        td{padding:6px 8px;border-bottom:1px solid #ddd;font-size:11px}
        .amount{text-align:right;font-family:'Consolas',monospace}
        .totals-row{background:#f5f5f5;font-weight:600}
        .sale-badge{background:#E8F5E9;color:#2E7D32;padding:2px 6px;border-radius:3px;font-size:10px;font-weight:500}
        .return-badge{background:#FFEBEE;color:#C62828;padding:2px 6px;border-radius:3px;font-size:10px;font-weight:500}
        .return-amount{color:#C62828}
        @media print{body{margin:15px}h1{font-size:16px}}
      </style></head><body>
      <h1>POS Sales Report</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Period: ${dateFrom ? formatDate(dateFrom) : 'Start'} to ${dateTo ? formatDate(dateTo) : 'End'} |
        Transactions: ${transactions.length}
      </div>
      <div class="summary">
        <div class="summary-item">
          <div class="summary-label">Total Sales</div>
          <div class="summary-value">${formatCurrency(summary.totalSalesAmount)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Total Returns</div>
          <div class="summary-value">${formatCurrency(summary.totalReturnsAmount)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Net Sales</div>
          <div class="summary-value">${formatCurrency(summary.netSales)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Cash</div>
          <div class="summary-value">${formatCurrency(paymentBreakdown.cashTotal)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Card</div>
          <div class="summary-value">${formatCurrency(paymentBreakdown.cardTotal)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Credit</div>
          <div class="summary-value">${formatCurrency(paymentBreakdown.creditTotal)}</div>
        </div>
      </div>
      <table>
        <thead><tr>
          <th>#</th>
          <th>Date</th>
          <th>Doc #</th>
          <th>Type</th>
          <th>Customer</th>
          <th>Items</th>
          <th>Payment Method</th>
          <th class="amount">Amount</th>
        </tr></thead>
        <tbody>
          ${sortedTransactions.map((t, idx) => {
            const amt = Number(t.netAmount) || Number(t.totalAmount) || 0;
            const isReturn = t.tranTypeCode === 'SR';
            return `
            <tr>
              <td>${idx + 1}</td>
              <td>${formatDate(t.docDate)}</td>
              <td>${t.docNumber || '-'}</td>
              <td><span class="${isReturn ? 'return-badge' : 'sale-badge'}">${isReturn ? 'Return' : 'Sale'}</span></td>
              <td>${t.partyName || 'Walk-in Customer'}</td>
              <td>${getItemsCount(t)}</td>
              <td>${getPaymentMethodDisplay(t)}</td>
              <td class="amount ${isReturn ? 'return-amount' : ''}">${isReturn ? '-' : ''}${formatCurrency(amt)}</td>
            </tr>
          `}).join('')}
          <tr class="totals-row">
            <td colspan="7" style="text-align:right">Net Sales (${sortedTransactions.length} transactions)</td>
            <td class="amount">${formatCurrency(summary.netSales)}</td>
          </tr>
        </tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  return (
    <PageLayout pageTitle="POS Sales Report" breadcrumb="Reports / Sale Reports / POS Sales Report">
      {/* Filters */}
      <FiltersCard>
        <FiltersGrid>
          <FilterGroup>
            <Label>Date From</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Date To</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Region</Label>
            <Select value={selectedRegionId} onChange={e => { setSelectedRegionId(e.target.value); setSelectedAreaId('all'); }}>
              <option value="all">All Regions</option>
              {regions.map(r => (
                <option key={r.id} value={r.id}>{r.regionName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Area</Label>
            <Select value={selectedAreaId} onChange={e => setSelectedAreaId(e.target.value)}>
              <option value="all">All Areas</option>
              {filteredAreas.map(a => (
                <option key={a.id} value={a.id}>{a.areaName}</option>
              ))}
            </Select>
          </FilterGroup>

          {counters.length > 0 && (
            <FilterGroup>
              <Label>Counter</Label>
              <Select value={selectedCounterId} onChange={e => setSelectedCounterId(e.target.value)}>
                <option value="all">All Counters</option>
                {counters.map(c => (
                  <option key={c.id} value={c.id}>{c.counterName} ({c.counterCode})</option>
                ))}
              </Select>
            </FilterGroup>
          )}
          {shifts.length > 0 && (
            <FilterGroup>
              <Label>Shift</Label>
              <Select value={selectedShiftId} onChange={e => setSelectedShiftId(e.target.value)}>
                <option value="all">All Shifts</option>
                {shifts.map(s => (
                  <option key={s.id} value={s.id}>{shiftOptionLabel(s)}</option>
                ))}
              </Select>
            </FilterGroup>
          )}
          <FilterGroup>
            <Label>Customer</Label>
            <Select value={selectedCustomerId} onChange={e => setSelectedCustomerId(e.target.value)}>
              <option value="all">All Customers</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>{c.companyName || c.name || 'Customer'}</option>
              ))}
            </Select>
          </FilterGroup>
        </FiltersGrid>

        <ButtonGroup>
          <Button onClick={handleExport} disabled={sortedTransactions.length === 0}>
            <FiDownload /> Export CSV
          </Button>
          <Button onClick={handlePrint} disabled={sortedTransactions.length === 0}>
            <FiPrinter /> Print
          </Button>
          <Button primary onClick={loadTransactions} disabled={loading}>
            <FiFilter /> {loading ? 'Loading...' : 'Generate Report'}
          </Button>
        </ButtonGroup>
      </FiltersCard>

      {/* Summary Cards Row */}
      {hasLoaded && (
        <SummaryCards>
          <SummaryCardStyled>
            <SummaryIcon color={colors.primary.main}>
              <FiShoppingCart />
            </SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Sales</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.totalSalesAmount)}</SummaryValue>
              <SummarySubtext>{formatNumber(summary.salesCount)} transactions</SummarySubtext>
            </SummaryInfo>
          </SummaryCardStyled>

          <SummaryCardStyled>
            <SummaryIcon color={colors.error.main}>
              <FiRotateCcw />
            </SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Returns</SummaryLabel>
              <SummaryValue style={{ color: colors.error.main }}>
                {formatCurrency(summary.totalReturnsAmount)}
              </SummaryValue>
              <SummarySubtext>{formatNumber(summary.returnsCount)} transactions</SummarySubtext>
            </SummaryInfo>
          </SummaryCardStyled>

          <SummaryCardStyled>
            <SummaryIcon color={colors.success.main}>
              <FiDollarSign />
            </SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Net Sales</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.netSales)}</SummaryValue>
            </SummaryInfo>
          </SummaryCardStyled>

          <SummaryCardStyled>
            <SummaryIcon color={colors.info.main}>
              <FiTrendingUp />
            </SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Average Sale</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.averageSale)}</SummaryValue>
            </SummaryInfo>
          </SummaryCardStyled>

          {summary.voidedCount > 0 && (
            <SummaryCardStyled style={{ background: '#FFF5F5', borderColor: '#FECACA' }}>
              <SummaryIcon color={colors.error.main}>
                <FiTrendingUp style={{ transform: 'rotate(180deg)' }} />
              </SummaryIcon>
              <SummaryInfo>
                <SummaryLabel>Voided (excluded)</SummaryLabel>
                <SummaryValue style={{ color: colors.error.main, textDecoration: 'line-through' }}>
                  {formatCurrency(summary.voidedAmount)}
                </SummaryValue>
                <SummarySubtext>{formatNumber(summary.voidedCount)} cancelled</SummarySubtext>
              </SummaryInfo>
            </SummaryCardStyled>
          )}
        </SummaryCards>
      )}

      {/* Payment Breakdown Cards Row */}
      {hasLoaded && (
        <PaymentCards>
          <PaymentCardStyled>
            <PaymentIcon color={colors.success.main}>
              <FiDollarSign />
            </PaymentIcon>
            <PaymentInfo>
              <PaymentLabel>Cash Collected</PaymentLabel>
              <PaymentValue>{formatCurrency(paymentBreakdown.cashTotal)}</PaymentValue>
            </PaymentInfo>
          </PaymentCardStyled>

          <PaymentCardStyled>
            <PaymentIcon color={colors.info.main}>
              <FiCreditCard />
            </PaymentIcon>
            <PaymentInfo>
              <PaymentLabel>Card Collected</PaymentLabel>
              <PaymentValue>{formatCurrency(paymentBreakdown.cardTotal)}</PaymentValue>
            </PaymentInfo>
          </PaymentCardStyled>

          <PaymentCardStyled>
            <PaymentIcon color={colors.warning.main}>
              <FiDollarSign />
            </PaymentIcon>
            <PaymentInfo>
              <PaymentLabel>On Credit</PaymentLabel>
              <PaymentValue>{formatCurrency(paymentBreakdown.creditTotal)}</PaymentValue>
            </PaymentInfo>
          </PaymentCardStyled>
        </PaymentCards>
      )}

      {/* Data Table */}
      <ContentCard>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <EmptyText>Loading POS transactions...</EmptyText>
            </EmptyState>
          ) : !hasLoaded ? (
            <EmptyState>
              <EmptyIcon><FiShoppingCart /></EmptyIcon>
              <EmptyText>Choose a date range, then click "Generate Report" to load POS sales.</EmptyText>
            </EmptyState>
          ) : sortedTransactions.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiShoppingCart /></EmptyIcon>
              <EmptyText>No POS transactions found for the selected date range</EmptyText>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th>#</Th>
                  <ThSortable
                    onClick={() => handleSort('docDate')}
                    $active={sortField === 'docDate'}
                  >
                    Date
                    <SortIcon>
                      {sortField === 'docDate'
                        ? (sortDirection === 'asc' ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />)
                        : <FiChevronDown size={12} style={{ opacity: 0.3 }} />
                      }
                    </SortIcon>
                  </ThSortable>
                  <ThSortable
                    onClick={() => handleSort('docNumber')}
                    $active={sortField === 'docNumber'}
                  >
                    Doc #
                    <SortIcon>
                      {sortField === 'docNumber'
                        ? (sortDirection === 'asc' ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />)
                        : <FiChevronDown size={12} style={{ opacity: 0.3 }} />
                      }
                    </SortIcon>
                  </ThSortable>
                  <Th>Type</Th>
                  {counters.length > 0 && <Th>Counter</Th>}
                  {shifts.length > 0 && <Th>Shift</Th>}
                  <ThSortable
                    onClick={() => handleSort('partyName')}
                    $active={sortField === 'partyName'}
                  >
                    Customer
                    <SortIcon>
                      {sortField === 'partyName'
                        ? (sortDirection === 'asc' ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />)
                        : <FiChevronDown size={12} style={{ opacity: 0.3 }} />
                      }
                    </SortIcon>
                  </ThSortable>
                  <Th align="center">Items</Th>
                  <Th align="right">Cash</Th>
                  <Th align="right">Card</Th>
                  <Th align="right">Credit</Th>
                  <Th align="right">Balance</Th>
                  <ThSortable
                    align="right"
                    onClick={() => handleSort('netAmount')}
                    $active={sortField === 'netAmount'}
                  >
                    Amount
                    <SortIcon>
                      {sortField === 'netAmount'
                        ? (sortDirection === 'asc' ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />)
                        : <FiChevronDown size={12} style={{ opacity: 0.3 }} />
                      }
                    </SortIcon>
                  </ThSortable>
                </Tr>
              </Thead>
              <Tbody>
                {sortedTransactions.map((t, idx) => {
                  const amt = Number(t.netAmount) || Number(t.totalAmount) || 0;
                  const isReturn = t.tranTypeCode === 'SR';
                  const isCancelled = t.status === 'Cancelled';
                  const rowStyle = isCancelled
                    ? { background: '#FFF5F5', opacity: 0.7 }
                    : {};
                  return (
                    <Tr key={t.id} style={rowStyle}>
                      <Td>{idx + 1}</Td>
                      <Td>{formatDate(t.docDate)}</Td>
                      <Td style={{ fontWeight: 500 }}>
                        {t.docNumber && t.id ? (
                          <span onClick={() => navigateToTransaction(navigate, t.tranTypeCode, t.id)} title="Open document"
                            style={{ color: colors.primary.main, cursor: 'pointer' }}>{t.docNumber}</span>
                        ) : (t.docNumber || '-')}
                      </Td>
                      <Td>
                        <TypeBadge $isReturn={isReturn}>
                          {isReturn ? 'Return' : 'Sale'}
                        </TypeBadge>
                        {isCancelled && (
                          <span style={{ marginLeft: 6, background: '#b00020', color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 10, letterSpacing: 0.5 }}>VOIDED</span>
                        )}
                      </Td>
                      {counters.length > 0 && <Td>{t.counterCode || '-'}</Td>}
                      {shifts.length > 0 && <Td>{shiftLabel(t.shiftId)}</Td>}
                      <Td>{t.partyName || 'Walk-in Customer'}</Td>
                      <Td align="center">{getItemsCount(t)}</Td>
                      {(() => {
                        const s = getPaymentSplit(t);
                        const sign = isReturn ? -1 : 1;
                        return (
                          <>
                            <AmountCell align="right">{s.cash ? formatCurrency(sign * s.cash) : '-'}</AmountCell>
                            <AmountCell align="right">{s.card ? formatCurrency(sign * s.card) : '-'}</AmountCell>
                            <AmountCell align="right">{s.credit ? formatCurrency(sign * s.credit) : '-'}</AmountCell>
                            <AmountCell align="right">{Number(t.balanceAmount) ? formatCurrency(Number(t.balanceAmount)) : '-'}</AmountCell>
                          </>
                        );
                      })()}
                      <AmountCell
                        align="right"
                        style={{
                          fontWeight: 600,
                          color: isCancelled ? '#999' : (isReturn ? colors.error.main : colors.text.primary),
                          textDecoration: isCancelled ? 'line-through' : 'none'
                        }}
                      >
                        {isReturn ? '-' : ''}{formatCurrency(amt)}
                      </AmountCell>
                    </Tr>
                  );
                })}

                {/* Totals Row */}
                <TotalsRow>
                  <Td colSpan={10 + (counters.length > 0 ? 1 : 0) + (shifts.length > 0 ? 1 : 0)} style={{ textAlign: 'right', fontWeight: 700 }}>
                    Net Sales ({sortedTransactions.length} transactions)
                  </Td>
                  <AmountCell align="right" style={{ fontWeight: 700 }}>
                    {formatCurrency(summary.netSales)}
                  </AmountCell>
                </TotalsRow>
              </Tbody>
            </Table>
          )}
        </TableContainer>
      </ContentCard>
    </PageLayout>
  );
};

// ============================================================
// Styled Components
// ============================================================

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }

  &::placeholder {
    color: ${colors.text.tertiary};
  }
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  ${props => props.primary ? `
    background: ${colors.primary.main};
    color: white;
    &:hover {
      background: ${colors.primary.dark};
    }
  ` : `
    background: ${colors.neutral[100]};
    color: ${colors.text.primary};
    &:hover {
      background: ${colors.neutral[200]};
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCardStyled = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SummaryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.color || colors.primary.main}15;
  color: ${props => props.color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const SummaryInfo = styled.div`
  min-width: 0;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: ${colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SummarySubtext = styled.div`
  font-size: 12px;
  color: ${colors.text.tertiary};
  margin-top: 2px;
`;

const PaymentCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const PaymentCardStyled = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const PaymentIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.color || colors.primary.main}15;
  color: ${props => props.color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const PaymentInfo = styled.div`
  min-width: 0;
`;

const PaymentLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
`;

const PaymentValue = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: ${colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: ${props => props.align || 'left'};
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.neutral[200]};
  white-space: nowrap;
`;

const ThSortable = styled(Th)`
  cursor: pointer;
  user-select: none;
  transition: color 0.2s;

  &:hover {
    color: ${colors.primary.main};
  }

  ${props => props.$active && `
    color: ${colors.primary.main};
  `}
`;

const SortIcon = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  vertical-align: middle;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.2s;

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const TotalsRow = styled.tr`
  background: ${colors.neutral[100]};
  border-top: 2px solid ${colors.neutral[300]};

  &:hover {
    background: ${colors.neutral[100]};
  }
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: ${colors.text.primary};
  text-align: ${props => props.align || 'left'};
`;

const AmountCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 13px;
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: ${props => props.$isReturn ? '#FFEBEE' : '#E8F5E9'};
  color: ${props => props.$isReturn ? '#C62828' : '#2E7D32'};
`;

const PaymentMethodBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: ${colors.neutral[100]};
  color: ${colors.text.secondary};
  text-transform: capitalize;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;
`;

export default POSSalesReport;
